export interface Volume {
  number: string
  title: string
  subtitle: string
  intro: string[]
  questions?: string[]
  highlight?: string
  afterHighlight?: string
  bulletsIntro: string
  bullets: string[]
  sequenceIntro?: string
  sequence?: string
  secondaryBulletsIntro?: string
  secondaryBullets?: string[]
  resultLead?: string
  resultQuote?: string
  result: string
  resultEmphasis?: string
  popular?: boolean
  popularLabel?: string
}

export const volumes: Volume[] = [
  {
    number: 'Volume 1',
    title: 'Professional Grooming Foundations & Safe Setup',
    subtitle: 'Build the foundation every grooming student needs.',
    intro: [
      'A new grooming student needs more than information about tools.',
      'They need a clear sequence that takes them from professional setup toward safe, organized and confident grooming work.',
      'This volume gives the instructor a structured way to teach the fundamentals instead of rebuilding every beginner lesson.',
    ],
    bulletsIntro: 'Inside:',
    bullets: [
      'Professional workstation setup',
      'Grooming table organization',
      'Essential equipment',
      'Tool preparation',
      'Dog assessment',
      'Coat assessment',
      'Safe positioning',
      'Grooming workflow',
      'Preparation routines',
      'Basic handling principles',
      'Instructor cues',
      'Beginner mistakes',
      'Foundational progressions',
    ],
    result:
      'Your students develop stronger professional foundations while you follow a clear and repeatable teaching progression.',
  },
  {
    number: 'Volume 2',
    title: 'Coat Types, Brushing, De-Shedding & Preparation',
    subtitle: 'Teach students to read the coat before choosing the technique.',
    intro: ['Tool control alone is not enough.', 'Students need to understand:'],
    questions: [
      'What coat type am I working with?',
      'What condition is the coat in?',
      'What preparation does it need?',
      'Which approach makes sense for this dog?',
    ],
    bulletsIntro: 'This volume helps you teach:',
    bullets: [
      'Short coats',
      'Long coats',
      'Double coats',
      'Curly coats',
      'Silky coats',
      'Wire coats',
      'Coat density',
      'Coat condition',
      'Brushing',
      'Combing',
      'De-shedding',
      'Detangling',
      'Drying',
      'Coat preparation',
      'Technique selection',
      'Common preparation mistakes',
    ],
    result:
      'Students begin making better grooming decisions because they understand the coat before they begin cutting.',
  },
  {
    number: 'Volume 3',
    title: 'Clipper Skills, Blades & Body Grooming',
    subtitle: 'Turn clipper work into clear visual teaching sequences.',
    intro: [
      'Clipper work can quickly become inconsistent when students do not understand pressure, direction, overlap and length.',
      'Instead of explaining everything verbally, this volume helps you break the technique down visually.',
    ],
    bulletsIntro: 'Includes:',
    bullets: [
      'Clipper handling',
      'Blade selection',
      'Guard comb selection',
      'Coat direction',
      'Body length',
      'Clipper angle',
      'Pressure control',
      'Overlapping passes',
      'Body zones',
      'Length consistency',
      'Visible clipper tracks',
      'Transition areas',
      'Body shaping',
      'Correct-vs-incorrect examples',
      'Instructor corrections',
    ],
    result: 'Students can see how the clipper should move before they attempt the technique themselves.',
  },
  {
    number: 'Volume 4',
    title: 'Scissor Skills, Blending & Professional Styling',
    subtitle:
      'Visual step-by-step instruction for the styling techniques students struggle with most.',
    intro: [],
    bulletsIntro: 'Includes:',
    bullets: [
      'Straight scissors',
      'Curved scissors',
      'Thinning shears',
      'Blending shears',
      'Scissor angles',
      'Comb lift',
      'Controlled cutting',
      'Body shaping',
      'Leg shaping',
      'Smooth transitions',
      'Symmetry',
      'Head shaping',
      'Contour building',
      'Finishing',
      'Common scissor mistakes',
      'Quick corrections',
    ],
    sequenceIntro: 'The material uses:',
    sequence:
      'Reference Shape → Tool Position → Cut Direction → Check → Correction → Final Shape',
    result:
      'Instead of telling students to “make it more balanced,” you can show them exactly what needs to change.',
  },
  {
    number: 'Volume 5',
    title: 'Face, Feet, Ears, Tail & Detail Work',
    subtitle: 'Help students progress from basic grooming to precise professional finishing.',
    intro: [],
    bulletsIntro: 'Includes:',
    bullets: [
      'Head balance',
      'Muzzle shaping',
      'Eye-area cleanup',
      'Ear styling',
      'Ear symmetry',
      'Front feet',
      'Rear feet',
      'Round-foot shaping',
      'Paw-area grooming',
      'Tail styling',
      'Facial proportions',
      'Detail transitions',
      'Final symmetry',
      'Finishing passes',
      'Professional polish',
    ],
    result:
      'Students learn to refine the areas that transform a basic groom into a cleaner and more intentional professional result.',
  },
  {
    number: 'Volume 6',
    title: 'Breed Styling, Corrections & Complete Grooming Workflow',
    subtitle: 'Stop teaching grooming techniques as disconnected skills.',
    intro: [
      'This is the practical heart of the complete system.',
      'Students learn how to bring the entire grooming process together:',
    ],
    highlight: 'ASSESS → PLAN → PREPARE → BODY → LEGS → HEAD → DETAIL → BALANCE → CORRECT → FINISH',
    bulletsIntro: 'Inside you’ll find:',
    bullets: [
      'Pre-groom assessment',
      'Style decision systems',
      'Seven body zones',
      'Silhouette building',
      'Length planning',
      'Coat direction',
      'Body clipper workflow',
      'Neck-to-body blending',
      'Chest shaping',
      'Topline creation',
      'Underline balance',
      'Front-leg columns',
      'Rear-leg shaping',
      'Body-to-leg transitions',
      'Round-foot systems',
      'Head-shape frameworks',
      'Muzzle balance',
      'Ear styling',
      'Tail styling',
      'Breed-focused examples',
      'Poodle-type styling',
      'Bichon-type styling',
      'Shih Tzu-type styling',
      'Yorkshire-type styling',
      'Schnauzer-type styling',
      'Doodle / curly-coat styling',
      'Multiple finish options',
      'Common grooming errors',
      'Correction priorities',
      'Student practical exercises',
      'Complete grooming workflow',
      'Practical assessment tools',
    ],
    resultLead: 'Instead of asking:',
    resultQuote: '“How do I explain this complete groom to my students?”',
    result: 'you open the system and already have the visual teaching sequence.',
    popular: true,
    popularLabel: 'MOST ADVANCED',
  },
]
